import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { T, FONT, MONO, radius, tint, alpha } from './poyais-ui/theme';
import { useIsMobile } from './poyais-ui/hooks';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Shopify Bağlı Değil': 'Shopify Not Connected',
  'Ayarlar sayfasından mağazanızı bağlayın.': 'Connect your store from the Settings page.',
  'Ayarlara Git': 'Go to Settings', 'Veri alınamadı': 'Could not load data', 'Hata': 'Error',
  'Satışlar': 'Sales', 'Reklam Performansı': 'Ad Performance', 'İadeler ve İptaller': 'Returns & Cancellations', 'Siparişler': 'Orders', 'Ürünler': 'Products',
  'Net Ciro': 'Net Revenue', 'Sipariş': 'Orders', 'Ort. Sepet': 'Avg. Order Value', 'Satılan Adet': 'Units Sold',
  'İade Tutarı': 'Refunded', 'İptal': 'Cancelled', 'Reklam Harcaması': 'Ad Spend', 'MER (Ciro / Reklam)': 'MER (Revenue / Ad Spend)',
  'önceki döneme göre': 'vs. previous period', 'Net ciro günlük': 'Daily net revenue', 'Günlük Trend': 'Daily Trend',
  'En Çok Satan Ürünler': 'Top Products', 'Trafik Kaynakları': 'Traffic Sources', 'Ürün': 'Product', 'Adet': 'Units', 'Ciro': 'Revenue',
  'Kaynak': 'Source', 'Pay': 'Share', 'Reklam': 'Paid', 'Organik / Doğrudan': 'Organic / Direct',
  'Brüt Satış': 'Gross Sales', 'İndirimler': 'Discounts', 'İadeler': 'Refunds', 'Kargo': 'Shipping', 'Vergi (KDV)': 'Tax',
  'Saate Göre Sipariş': 'Orders by Hour', 'Güne Göre Sipariş': 'Orders by Weekday', 'Ödeme Yöntemleri': 'Payment Methods',
  'İndirim Kodları': 'Discount Codes', 'Kod': 'Code', 'Kullanım': 'Uses', 'Ödeme': 'Payment',
  'Pzt': 'Mon', 'Sal': 'Tue', 'Çar': 'Wed', 'Per': 'Thu', 'Cum': 'Fri', 'Cmt': 'Sat', 'Paz': 'Sun',
  'Sipariş No': 'Order', 'Tarih': 'Date', 'Durum': 'Status', 'Teslimat': 'Fulfillment', 'Tutar': 'Amount', 'Net': 'Net',
  'Tümü': 'All', 'Ödendi': 'Paid', 'Beklemede': 'Pending', 'Kısmi iade': 'Partially refunded', 'İade': 'Refunded', 'Onaylandı': 'Authorized',
  'Gönderildi': 'Fulfilled', 'Bekliyor': 'Unfulfilled', 'Sipariş ara…': 'Search orders…', 'Ürün ara…': 'Search products…',
  'Dışa Aktar': 'Export', 'Bu dönemde sipariş yok.': 'No orders in this period.', 'Veri yok.': 'No data.',
  'Son 500 sipariş gösteriliyor': 'Showing the latest 500 orders', 'Gönderilmeyi Bekleyen': 'Awaiting Shipment',
  'Aktif Ürün': 'Active Products', 'Stok Bitti': 'Out of Stock', 'Stok Azalıyor': 'Low Stock (≤5)', 'Dönemde Satışı Yok': 'No Sales in Period',
  'Stok': 'Stock', 'Fiyat': 'Price', 'Satış': 'Sales', 'İade Oranı': 'Return Rate', 'Satış (ciro)': 'Revenue', 'Stok Azalan': 'Low stock',
  'Satışı Olmayan': 'No sales', 'Stok takibi yok': 'Not tracked', 'Aktif': 'Active', 'Taslak': 'Draft', 'Arşiv': 'Archived',
  'İptal Oranı': 'Cancel Rate', 'İade Oranı (sipariş)': 'Refund Rate (orders)', 'Tam İade': 'Full Refunds', 'Kısmi İade': 'Partial Refunds',
  'İade tutarı / ciro': 'Refunds / revenue', 'En Çok İade Edilen Ürünler': 'Most Returned Products', 'İade edilen': 'Returned', 'Satılan': 'Sold',
  'Günlük İptal ve İade': 'Daily Cancellations & Refunds', 'Son İptal ve İadeler': 'Recent Cancellations & Refunds', 'Tür': 'Type',
  'Reklam geliri (Shopify)': 'Revenue (Shopify)', 'Platform Karşılaştırması': 'Platform Comparison', 'Platform': 'Platform',
  'Harcama': 'Spend', 'Platformun bildirdiği': 'Platform-reported', 'Shopify doğrulaması': 'Shopify verified', 'Sipariş (Shopify)': 'Orders (Shopify)',
  'Harcama ve Ciro': 'Spend vs Revenue', 'Günlük MER': 'Daily MER', 'CPA (Shopify siparişi başına)': 'CPA (per Shopify order)',
  'Reklam / Ciro': 'Ad spend / Revenue', 'Kampanyalar': 'Campaigns', 'Kampanya': 'Campaign',
  'Reklam harcama verisi bulunamadı. Meta Ads veya Google Ads bağlayın.': 'No ad spend data found. Connect Meta Ads or Google Ads.',
  'Reklam kaynaklı sipariş': 'Orders from ads', 'Reklam kaynaklı ciro': 'Revenue from ads', 'Doğrudan': 'Direct',
  'ROAS (platform)': 'ROAS (platform)', 'ROAS (Shopify)': 'ROAS (Shopify)',
});

const API = '/api/v1';

const fmtN = (v: number) => Number(v || 0).toLocaleString('tr-TR');
const fmtPct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const x2 = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`);

function money(v: number, cur?: string, compact = false) {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur || 'TRY', maximumFractionDigits: 0, notation: compact ? 'compact' : 'standard' }).format(v || 0);
  } catch { return `${fmtN(Math.round(v || 0))}`; }
}

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0} /></div>; }

function DisconnectedState() {
  const { t } = useLang();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14, fontFamily: FONT }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t('Shopify Bağlı Değil')}</div>
      <div style={{ fontSize: 13, color: T.textDim }}>{t('Ayarlar sayfasından mağazanızı bağlayın.')}</div>
      <Button variant="solid" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'settings' }))}>{t('Ayarlara Git')}</Button>
    </div>
  );
}

function ErrorBox({ error }: { error: string }) {
  const { t } = useLang();
  return <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}><div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div><div style={{ fontSize: 12, color: T.textMid }}>{error}</div></div>;
}

function useReport(path: string, clientId: number, start?: string, end?: string) {
  const { t } = useLang();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true); setError(null); setData(null);
    const params: any = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    axios.get(`${API}/shopify/${clientId}/${path}`, { params })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || t('Veri alınamadı')); setLoading(false); });
  }, [clientId, start, end, path]);
  return { data, loading, error };
}

// ── Ortak parçalar ──────────────────────────────────────────────────────────────────────
function Delta({ pct, invert }: { pct: number | null | undefined; invert?: boolean }) {
  const { t } = useLang();
  if (pct === null || pct === undefined) return null;
  const good = invert ? pct < 0 : pct > 0;
  const color = pct === 0 ? T.textDim : good ? T.greenText : T.redText;
  return <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 4 }}>{pct > 0 ? '↑' : pct < 0 ? '↓' : '·'} %{Math.abs(pct).toLocaleString('tr-TR')} <span style={{ fontWeight: 500, color: T.textDim }}>{t('önceki döneme göre')}</span></div>;
}

type Kpi = { label: string; value: string; color?: string; delta?: number | null; invert?: boolean; sub?: string };
function Kpis({ items }: { items: Kpi[] }) {
  const isMobile = useIsMobile();
  return (
    <Grid min={isMobile ? 140 : 170} gap={isMobile ? 8 : 14}>
      {items.map((k, i) => (
        <Card key={i} pad={18}>
          <Eyebrow color={k.color || T.accent}>{k.label}</Eyebrow>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: MONO, fontVariantNumeric: 'tabular-nums', marginTop: 8 }}>{k.value}</div>
          <Delta pct={k.delta} invert={k.invert} />
          {k.sub && <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>{k.sub}</div>}
        </Card>
      ))}
    </Grid>
  );
}

function Panel({ title, extra, children, pad = 20 }: { title: string; extra?: any; children: any; pad?: number }) {
  return (
    <Card pad={0}>
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</div>{extra}
      </div>
      <div style={{ padding: pad }}>{children}</div>
    </Card>
  );
}

type Col = { label: string; align?: 'left' | 'right'; render: (r: any) => any };
function Table({ cols, rows, empty, max }: { cols: Col[]; rows: any[]; empty: string; max?: number }) {
  const { t } = useLang();
  return (
    <div style={{ overflowX: 'auto' as const, maxHeight: max, overflowY: max ? 'auto' as const : undefined }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth: 480 }}>
        <thead style={{ background: T.card, position: 'sticky' as const, top: 0 }}>
          <tr>{cols.map((c, i) => <th key={i} style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '0.08em', textAlign: c.align || 'left', whiteSpace: 'nowrap' as const, borderBottom: `1px solid ${T.border}` }}>{t(c.label)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={cols.length} style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{empty}</td></tr>
            : rows.map((r, ri) => (
              <tr key={ri} style={{ borderBottom: `1px solid ${T.card}` }} onMouseEnter={e => (e.currentTarget.style.background = T.card)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                {cols.map((c, ci) => <td key={ci} style={{ padding: '11px 14px', textAlign: c.align || 'left', fontSize: 12, color: T.text, fontVariantNumeric: 'tabular-nums' }}>{c.render(r)}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function Chips<K extends string>({ value, onChange, options }: { value: K; onChange: (k: K) => void; options: { key: K; label: string }[] }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
      {options.map(o => (
        <Button key={o.key} variant={value === o.key ? 'soft' : 'ghost'} color={T.accent} onClick={() => onChange(o.key)} style={{ padding: '4px 12px', fontSize: 11, fontWeight: value === o.key ? 700 : 500 }}>{o.label}</Button>
      ))}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ padding: '7px 12px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: 12, fontFamily: FONT, outline: 'none', minWidth: 180 }} />;
}

function ExportBtn({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  const { t } = useLang();
  return <Button variant="ghost" color={T.accent} disabled={disabled} onClick={onClick} style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}><Icon.download size={13} />{t('Dışa Aktar')}</Button>;
}

const AXIS = { tick: { fontSize: 10, fill: T.textDim }, axisLine: false, tickLine: false } as const;
const tickDay = (v: string) => (v || '').slice(5).replace('-', '.');
const Note = ({ children }: { children: any }) => <div style={{ fontSize: 12, color: T.textMid, background: T.card, border: `1px solid ${T.border}`, borderRadius: radius.lg, padding: '10px 14px', lineHeight: 1.55 }}>{children}</div>;

function MissingNote({ names }: { names: string[] }) {
  const label: Record<string, string> = { meta: 'Meta Ads', google: 'Google Ads', tiktok: 'TikTok Ads' };
  return <Note>⚠ {names.map(n => label[n] || n).join(', ')} harcama verisi şu an alınamadı (platform geçici olarak yanıt vermedi). Reklam harcaması, MER ve ROAS eksik görünebilir; birkaç dakika sonra sayfayı yenileyin.</Note>;
}

function SourceBadge({ name, paid }: { name: string; paid?: boolean }) {
  const { t } = useLang();
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ fontWeight: 600 }}>{name === 'Doğrudan' ? t('Doğrudan') : name}</span>{paid && <Badge color={T.accent}>{t('Reklam')}</Badge>}</span>;
}

function TrendChart({ data, dataKey, cur, color, height = 230, isMoney = true }: { data: any[]; dataKey: string; cur?: string; color: string; height?: number; isMoney?: boolean }) {
  const { t } = useLang();
  const gid = `g_${dataKey}_${color.replace(/\W/g, '')}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.28} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" {...AXIS} tickFormatter={tickDay} minTickGap={22} />
        <YAxis {...AXIS} width={52} allowDecimals={false} tickFormatter={(v: number) => isMoney ? money(v, cur, true) : fmtN(v)} />
        <Tooltip formatter={(v: any) => isMoney ? money(Number(v), cur) : fmtN(Number(v))} labelFormatter={l => `${t('Tarih')}: ${l}`} />
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#${gid})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const STATUS: Record<string, [string, string]> = {
  paid: ['Ödendi', 'green'], pending: ['Beklemede', 'amber'], authorized: ['Onaylandı', 'blue'], refunded: ['İade', 'red'],
  partially_refunded: ['Kısmi iade', 'amber'], cancelled: ['İptal', 'red'],
};
function statusBadge(st: string, t: (s: string) => string) {
  const [label, col] = STATUS[st] || [st || '—', 'dim'];
  const color = col === 'green' ? T.green : col === 'red' ? T.red : col === 'amber' ? T.amber : col === 'blue' ? T.blue : T.textDim;
  return <Badge color={color}>{t(label)}</Badge>;
}

// ── GENEL BAKIŞ ─────────────────────────────────────────────────────────────────────────
function OverviewTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('overview', clientId, start, end);
  const [metric, setMetric] = useState<'net' | 'orders'>('net');
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Net Ciro'), value: money(d.net, c), color: T.accent, delta: d.net_change },
        { label: t('Sipariş'), value: fmtN(d.orders), color: T.blue, delta: d.orders_change, sub: d.cancelled ? `${d.cancelled} ${t('İptal').toLowerCase()}` : undefined },
        { label: t('Ort. Sepet'), value: money(d.aov, c), color: T.textDim, delta: d.aov_change },
        { label: t('İade Tutarı'), value: money(d.refund, c), color: T.red, sub: `${fmtPct(d.refund_rate)} ${t('İade tutarı / ciro').toLowerCase()}` },
        { label: t('Reklam Harcaması'), value: d.spend ? money(d.spend, c) : '—', color: T.accent },
        { label: t('MER (Ciro / Reklam)'), value: x2(d.mer), color: T.green },
      ]} />
      {d.ads_missing?.length > 0 && <MissingNote names={d.ads_missing} />}
      <Panel title={t('Günlük Trend')} extra={<Chips value={metric} onChange={setMetric} options={[{ key: 'net', label: t('Ciro') }, { key: 'orders', label: t('Sipariş') }]} />}>
        <TrendChart data={d.daily} dataKey={metric} cur={c} color={metric === 'net' ? T.accent : T.blue} isMoney={metric === 'net'} />
      </Panel>
      <Grid min={340} gap={14}>
        <Panel title={t('En Çok Satan Ürünler')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.top_products} cols={[
            { label: 'Ürün', render: (r: any) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
            { label: 'Adet', align: 'right', render: (r: any) => fmtN(r.qty) },
            { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          ]} />
        </Panel>
        <Panel title={t('Trafik Kaynakları')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.sources} cols={[
            { label: 'Kaynak', render: (r: any) => <SourceBadge name={r.source} paid={r.paid_orders > 0 && r.paid_orders === r.orders} /> },
            { label: 'Sipariş', align: 'right', render: (r: any) => fmtN(r.orders) },
            { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          ]} />
        </Panel>
      </Grid>
    </div>
  );
}

// ── SATIŞLAR ────────────────────────────────────────────────────────────────────────────
function SalesTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('sales', clientId, start, end);
  const [metric, setMetric] = useState<'net' | 'orders'>('net');
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  const wd = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  const weekdays = d.weekdays.map((w: any) => ({ ...w, label: t(wd[w.day]) }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Brüt Satış'), value: money(d.gross, c), color: T.textDim },
        { label: t('İndirimler'), value: money(d.discounts, c), color: T.amber },
        { label: t('İadeler'), value: money(d.refund, c), color: T.red },
        { label: t('Net Ciro'), value: money(d.net, c), color: T.accent, delta: d.net_change },
        { label: t('Sipariş'), value: fmtN(d.orders), color: T.blue, delta: d.orders_change },
        { label: t('Ort. Sepet'), value: money(d.aov, c), color: T.textDim, delta: d.aov_change },
        { label: t('Satılan Adet'), value: fmtN(d.units), color: T.textDim },
      ]} />
      <Panel title={t('Günlük Trend')} extra={<Chips value={metric} onChange={setMetric} options={[{ key: 'net', label: t('Ciro') }, { key: 'orders', label: t('Sipariş') }]} />}>
        <TrendChart data={d.daily} dataKey={metric} cur={c} color={metric === 'net' ? T.accent : T.blue} isMoney={metric === 'net'} />
      </Panel>
      <Grid min={340} gap={14}>
        <Panel title={t('Saate Göre Sipariş')}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={d.hours} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" {...AXIS} interval={2} tickFormatter={(h: number) => `${String(h).padStart(2, '0')}`} />
              <YAxis {...AXIS} allowDecimals={false} />
              <Tooltip formatter={(v: any, n: any) => n === 'net' ? money(Number(v), c) : fmtN(Number(v))} labelFormatter={h => `${String(h).padStart(2, '0')}:00`} />
              <Bar dataKey="orders" name={t('Sipariş')} fill={T.blue} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title={t('Güne Göre Sipariş')}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekdays} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} allowDecimals={false} />
              <Tooltip formatter={(v: any) => fmtN(Number(v))} />
              <Bar dataKey="orders" name={t('Sipariş')} fill={T.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Grid>
      <Panel title={t('En Çok Satan Ürünler')} pad={0}>
        <Table empty={t('Veri yok.')} rows={d.top_products} cols={[
          { label: 'Ürün', render: (r: any) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
          { label: 'Adet', align: 'right', render: (r: any) => fmtN(r.qty) },
          { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
        ]} />
      </Panel>
      <Grid min={340} gap={14}>
        <Panel title={t('Ödeme Yöntemleri')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.payments} cols={[
            { label: 'Ödeme', render: (r: any) => <span style={{ fontWeight: 600 }}>{r.payment}</span> },
            { label: 'Sipariş', align: 'right', render: (r: any) => fmtN(r.orders) },
            { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          ]} />
        </Panel>
        <Panel title={t('İndirim Kodları')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.discount_codes} cols={[
            { label: 'Kod', render: (r: any) => <span style={{ fontWeight: 600, fontFamily: MONO }}>{r.code}</span> },
            { label: 'Kullanım', align: 'right', render: (r: any) => fmtN(r.orders) },
            { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          ]} />
        </Panel>
      </Grid>
    </div>
  );
}

// ── SİPARİŞLER ──────────────────────────────────────────────────────────────────────────
function OrdersTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('orders', clientId, start, end);
  const [filter, setFilter] = useState<'all' | 'paid' | 'pending' | 'refund' | 'cancelled' | 'unfulfilled'>('all');
  const [q, setQ] = useState('');
  const rows = useMemo(() => (d?.orders || []).filter((o: any) => {
    if (q && !String(o.name).toLowerCase().includes(q.toLowerCase().replace('#', ''))) return false;
    if (filter === 'paid') return o.status === 'paid';
    if (filter === 'pending') return o.status === 'pending' || o.status === 'authorized';
    if (filter === 'refund') return o.refund > 0;
    if (filter === 'cancelled') return o.status === 'cancelled';
    if (filter === 'unfulfilled') return o.status !== 'cancelled' && o.fulfillment !== 'fulfilled';
    return true;
  }), [d, filter, q]);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  const exp = () => exportCsv('shopify-siparisler.csv', rows.map((o: any) => ({ ...o, created_at: (o.created_at || '').slice(0, 16).replace('T', ' ') })), [
    { key: 'name', label: 'Sipariş No' }, { key: 'created_at', label: 'Tarih' }, { key: 'status', label: 'Durum' }, { key: 'fulfillment', label: 'Teslimat' },
    { key: 'source', label: 'Kaynak' }, { key: 'payment', label: 'Ödeme' }, { key: 'units', label: 'Adet' }, { key: 'total', label: 'Tutar' }, { key: 'refund', label: 'İade' }, { key: 'net', label: 'Net' },
  ]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Sipariş'), value: fmtN(d.valid_orders), color: T.blue, sub: d.cancelled ? `+ ${d.cancelled} ${t('İptal').toLowerCase()}` : undefined },
        { label: t('Net Ciro'), value: money(d.net, c), color: T.accent },
        { label: t('Ort. Sepet'), value: money(d.aov, c), color: T.textDim },
        { label: t('Gönderilmeyi Bekleyen'), value: fmtN(d.unfulfilled), color: T.amber },
      ]} />
      <Panel pad={0} title={t('Sipariş')} extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
          <SearchBox value={q} onChange={setQ} placeholder={t('Sipariş ara…')} />
          <ExportBtn onClick={exp} disabled={rows.length === 0} />
        </div>}>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' as const, alignItems: 'center' }}>
          <Chips value={filter} onChange={setFilter} options={[
            { key: 'all', label: t('Tümü') }, { key: 'paid', label: t('Ödendi') }, { key: 'pending', label: t('Beklemede') },
            { key: 'unfulfilled', label: t('Bekliyor') }, { key: 'refund', label: t('İade') }, { key: 'cancelled', label: t('İptal') },
          ]} />
          {d.total_orders > d.shown && <span style={{ fontSize: 11, color: T.textDim }}>{t('Son 500 sipariş gösteriliyor')}</span>}
        </div>
        <Table max={620} empty={t('Bu dönemde sipariş yok.')} rows={rows} cols={[
          { label: 'Sipariş No', render: (r: any) => <span style={{ fontWeight: 700 }}>{r.name}</span> },
          { label: 'Tarih', render: (r: any) => (r.created_at || '').slice(0, 16).replace('T', ' ') },
          { label: 'Durum', render: (r: any) => statusBadge(r.status, t) },
          { label: 'Teslimat', render: (r: any) => r.status === 'cancelled' ? '—' : <Badge color={r.fulfillment === 'fulfilled' ? T.green : T.amber}>{r.fulfillment === 'fulfilled' ? t('Gönderildi') : t('Bekliyor')}</Badge> },
          { label: 'Kaynak', render: (r: any) => <SourceBadge name={r.source} paid={r.paid_ad} /> },
          { label: 'Adet', align: 'right', render: (r: any) => fmtN(r.units) },
          { label: 'Tutar', align: 'right', render: (r: any) => money(r.total, c) },
          { label: 'Net', align: 'right', render: (r: any) => <span style={{ fontWeight: 700, color: r.status === 'cancelled' ? T.textDim : T.text }}>{money(r.net, c)}{r.refund > 0 && <span style={{ color: T.redText, fontWeight: 500 }}> (−{money(r.refund, c)})</span>}</span> },
        ]} />
      </Panel>
    </div>
  );
}

// ── ÜRÜNLER ─────────────────────────────────────────────────────────────────────────────
function ProductsTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('products', clientId, start, end);
  const [filter, setFilter] = useState<'all' | 'low' | 'nosales' | 'draft'>('all');
  const [sort, setSort] = useState<'net' | 'sold' | 'inventory' | 'return_rate'>('net');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(50);
  const rows = useMemo(() => {
    const list = (d?.products || []).filter((p: any) => {
      if (q && !p.title.toLowerCase().includes(q.toLowerCase())) return false;
      if (filter === 'low') return p.status === 'active' && p.tracked && p.inventory <= 5;
      if (filter === 'nosales') return p.status === 'active' && p.sold === 0;
      if (filter === 'draft') return p.status !== 'active';
      return true;
    });
    return [...list].sort((a: any, b: any) => sort === 'inventory' ? a.inventory - b.inventory : b[sort] - a[sort]);
  }, [d, filter, sort, q]);
  useEffect(() => setLimit(50), [filter, sort, q]);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  const price = (p: any) => p.price_min === p.price_max ? money(p.price_min, c) : `${money(p.price_min, c)} – ${money(p.price_max, c)}`;
  const exp = () => exportCsv('shopify-urunler.csv', rows, [
    { key: 'title', label: 'Ürün' }, { key: 'type', label: 'Tür' }, { key: 'status', label: 'Durum' }, { key: 'price_min', label: 'Fiyat' },
    { key: 'inventory', label: 'Stok' }, { key: 'sold', label: 'Satılan' }, { key: 'net', label: 'Ciro' }, { key: 'return_rate', label: 'İade Oranı %' },
  ]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Aktif Ürün'), value: fmtN(d.active), color: T.accent, sub: `${fmtN(d.total)} ${t('Ürün').toLowerCase()}` },
        { label: t('Stok Bitti'), value: fmtN(d.out_of_stock), color: T.red },
        { label: t('Stok Azalıyor'), value: fmtN(d.low_stock), color: T.amber },
        { label: t('Dönemde Satışı Yok'), value: fmtN(d.no_sales), color: T.textDim },
      ]} />
      <Panel pad={0} title={t('Ürün')} extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
          <SearchBox value={q} onChange={setQ} placeholder={t('Ürün ara…')} />
          <select value={sort} onChange={e => setSort(e.target.value as any)} style={{ padding: '7px 10px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: 12, fontFamily: FONT }}>
            <option value="net">{t('Satış (ciro)')}</option><option value="sold">{t('Satılan')}</option>
            <option value="inventory">{t('Stok')}</option><option value="return_rate">{t('İade Oranı')}</option>
          </select>
          <ExportBtn onClick={exp} disabled={rows.length === 0} />
        </div>}>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${T.border}` }}>
          <Chips value={filter} onChange={setFilter} options={[
            { key: 'all', label: t('Tümü') }, { key: 'low', label: t('Stok Azalan') }, { key: 'nosales', label: t('Satışı Olmayan') }, { key: 'draft', label: `${t('Taslak')} / ${t('Arşiv')}` },
          ]} />
        </div>
        <Table max={640} empty={t('Veri yok.')} rows={rows.slice(0, limit)} cols={[
          { label: 'Ürün', render: (p: any) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {p.image ? <img src={p.image} alt="" width={34} height={34} style={{ borderRadius: 6, objectFit: 'cover' as const, flexShrink: 0 }} /> : <div style={{ width: 34, height: 34, borderRadius: 6, background: T.card, flexShrink: 0 }} />}
              <div><div style={{ fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.title}</div>
                <div style={{ fontSize: 10, color: T.textDim }}>{p.type || '—'} · {p.variants} varyant{p.status !== 'active' && <> · <span style={{ color: T.amber }}>{p.status === 'draft' ? t('Taslak') : t('Arşiv')}</span></>}</div></div>
            </div>) },
          { label: 'Fiyat', align: 'right', render: price },
          { label: 'Stok', align: 'right', render: (p: any) => !p.tracked ? <span style={{ color: T.textDim }}>{t('Stok takibi yok')}</span> : <span style={{ fontWeight: 700, color: p.inventory <= 0 ? T.redText : p.inventory <= 5 ? T.amberText : T.text }}>{fmtN(p.inventory)}</span> },
          { label: 'Satılan', align: 'right', render: (p: any) => fmtN(p.sold) },
          { label: 'Ciro', align: 'right', render: (p: any) => p.net ? money(p.net, c) : '—' },
          { label: 'İade Oranı', align: 'right', render: (p: any) => p.sold ? <span style={{ color: p.return_rate >= 20 ? T.redText : T.text }}>{fmtPct(p.return_rate)}</span> : '—' },
        ]} />
        {rows.length > limit && <div style={{ padding: 14, textAlign: 'center' as const }}><Button variant="ghost" color={T.accent} onClick={() => setLimit(l => l + 100)}>+ {Math.min(100, rows.length - limit)}</Button></div>}
      </Panel>
    </div>
  );
}

// ── İADELER VE İPTALLER ─────────────────────────────────────────────────────────────────
function ReturnsTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('returns', clientId, start, end);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('İade Tutarı'), value: money(d.refund_amount, c), color: T.red, sub: `${fmtPct(d.refund_amount_rate)} ${t('İade tutarı / ciro').toLowerCase()}` },
        { label: t('İade'), value: fmtN(d.refunded), color: T.red, sub: `${fmtPct(d.refunded_rate)} · ${d.full_refunds} ${t('Tam İade').toLowerCase()}, ${d.partial_refunds} ${t('Kısmi İade').toLowerCase()}` },
        { label: t('İptal'), value: fmtN(d.cancelled), color: T.amber, sub: `${fmtPct(d.cancelled_rate)} ${t('İptal Oranı').toLowerCase()}` },
        { label: t('Sipariş'), value: fmtN(d.orders), color: T.blue },
      ]} />
      <Panel title={t('Günlük İptal ve İade')}>
        {d.daily.length === 0 ? <div style={{ color: T.textDim, fontSize: 12.5 }}>{t('Veri yok.')}</div> : (
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={d.daily} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" {...AXIS} tickFormatter={tickDay} minTickGap={20} />
              <YAxis {...AXIS} allowDecimals={false} />
              <Tooltip labelFormatter={l => `${t('Tarih')}: ${l}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="refunded" name={t('İade')} stackId="a" fill={T.red} />
              <Bar dataKey="cancelled" name={t('İptal')} stackId="a" fill={T.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>)}
      </Panel>
      <Grid min={340} gap={14}>
        <Panel title={t('En Çok İade Edilen Ürünler')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.top_returned} cols={[
            { label: 'Ürün', render: (r: any) => <span style={{ fontWeight: 600 }}>{r.title}</span> },
            { label: 'İade edilen', align: 'right', render: (r: any) => fmtN(r.returned) },
            { label: 'Satılan', align: 'right', render: (r: any) => fmtN(r.sold) },
            { label: 'İade Oranı', align: 'right', render: (r: any) => <span style={{ color: r.return_rate >= 30 ? T.redText : T.text, fontWeight: 700 }}>{fmtPct(r.return_rate)}</span> },
          ]} />
        </Panel>
        <Panel title={t('Son İptal ve İadeler')} pad={0}>
          <Table max={360} empty={t('Veri yok.')} rows={d.recent} cols={[
            { label: 'Sipariş No', render: (r: any) => <span style={{ fontWeight: 700 }}>{r.name}</span> },
            { label: 'Tarih', render: (r: any) => (r.created_at || '').slice(0, 10) },
            { label: 'Tür', render: (r: any) => <Badge color={r.kind === 'cancelled' ? T.amber : T.red}>{r.kind === 'cancelled' ? t('İptal') : t('İade')}</Badge> },
            { label: 'Tutar', align: 'right', render: (r: any) => money(r.amount, c) },
          ]} />
        </Panel>
      </Grid>
    </div>
  );
}

// ── TRAFİK KAYNAKLARI ───────────────────────────────────────────────────────────────────
function SourcesTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('sources', clientId, start, end);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Reklam kaynaklı sipariş'), value: fmtN(d.paid_orders), color: T.accent, sub: `/ ${fmtN(d.orders)} ${t('Sipariş').toLowerCase()}` },
        { label: t('Reklam kaynaklı ciro'), value: money(d.paid_net, c), color: T.green },
        { label: t('Organik / Doğrudan'), value: fmtN(d.orders - d.paid_orders), color: T.textDim },
      ]} />
      {d.unresolved > 0 && <Note>{d.unresolved} sipariş, Meta reklam bağlantısındaki doldurulmamış etiketle (<code>{'{{site_source_name}}'}</code>) geldi. Meta reklamlarında “URL parametreleri”ni <code>utm_source=ig&amp;utm_campaign={'{{campaign.name}}'}</code> biçiminde güncelleyin; böylece kampanya bazlı sonuçlar eksiksiz görünür.</Note>}
      <Panel title={t('Trafik Kaynakları')} pad={0}>
        <Table empty={t('Veri yok.')} rows={d.sources} cols={[
          { label: 'Kaynak', render: (r: any) => <SourceBadge name={r.source} paid={r.paid_orders > 0 && r.paid_orders === r.orders} /> },
          { label: 'Sipariş', align: 'right', render: (r: any) => fmtN(r.orders) },
          { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          { label: 'Ort. Sepet', align: 'right', render: (r: any) => money(r.aov, c) },
          { label: 'Pay', align: 'right', render: (r: any) => fmtPct(r.share) },
        ]} />
      </Panel>
      <Panel title={t('Kampanyalar')} pad={0}>
        <Table empty={t('Veri yok.')} rows={d.campaigns} cols={[
          { label: 'Kampanya', render: (r: any) => <span style={{ fontWeight: 600 }}>{r.campaign}</span> },
          { label: 'Kaynak', render: (r: any) => r.source },
          { label: 'Sipariş', align: 'right', render: (r: any) => fmtN(r.orders) },
          { label: 'Ciro', align: 'right', render: (r: any) => money(r.net, c) },
          { label: 'Ort. Sepet', align: 'right', render: (r: any) => money(r.aov, c) },
        ]} />
      </Panel>
    </div>
  );
}

// ── REKLAM PERFORMANSI ──────────────────────────────────────────────────────────────────
function AdsSalesTab({ clientId, start, end }: any) {
  const { t } = useLang();
  const { data: d, loading, error } = useReport('ads-vs-sales', clientId, start, end);
  if (loading) return <Loader />;
  if (error) return <ErrorBox error={error} />;
  const c = d.currency;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Kpis items={[
        { label: t('Reklam Harcaması'), value: d.has_spend ? money(d.spend, c) : '—', color: T.accent },
        { label: t('Net Ciro'), value: money(d.revenue, c), color: T.green, sub: `${fmtN(d.orders)} ${t('Sipariş').toLowerCase()}` },
        { label: t('MER (Ciro / Reklam)'), value: x2(d.mer), color: T.blue, sub: 'Shopify net ciro ÷ toplam reklam harcaması' },
        { label: t('CPA (Shopify siparişi başına)'), value: d.cpa ? money(d.cpa, c) : '—', color: T.textDim },
        { label: t('Reklam / Ciro'), value: fmtPct(d.ad_share), color: T.textDim },
      ]} />
      {d.missing?.length > 0 && <MissingNote names={d.missing} />}
      {!d.has_spend && <Note>{t('Reklam harcama verisi bulunamadı. Meta Ads veya Google Ads bağlayın.')}</Note>}
      {d.platforms.length > 0 && (
        <Panel title={t('Platform Karşılaştırması')} pad={0}>
          <Table empty={t('Veri yok.')} rows={d.platforms} cols={[
            { label: 'Platform', render: (r: any) => <span style={{ fontWeight: 700 }}>{r.label}</span> },
            { label: 'Harcama', align: 'right', render: (r: any) => money(r.spend, c) },
            { label: 'Platformun bildirdiği', align: 'right', render: (r: any) => <span>{money(r.reported_revenue, c)} <span style={{ color: T.textDim }}>· {x2(r.reported_roas)}</span></span> },
            { label: 'Shopify doğrulaması', align: 'right', render: (r: any) => <span>{money(r.shopify_revenue, c)} <span style={{ color: T.textDim }}>· {x2(r.shopify_roas)}</span></span> },
            { label: 'Sipariş (Shopify)', align: 'right', render: (r: any) => fmtN(r.shopify_orders) },
            { label: 'CPA (Shopify siparişi başına)', align: 'right', render: (r: any) => r.cpa ? money(r.cpa, c) : '—' },
          ]} />
        </Panel>
      )}
      {d.platforms.length > 0 && <Note>
        <b>Platformun bildirdiği</b> değer reklam platformunun kendi dönüşüm ölçümüdür (görüntüleme sonrası dönüşümler dahil olabilir, iadeleri düşmez). <b>Shopify doğrulaması</b> ise yalnızca UTM / tıklama kimliği ile o platformdan geldiği kesin olan siparişlerin net cirosudur; gerçek değer genellikle ikisinin arasındadır.
        {d.unresolved > 0 && <> {d.unresolved} siparişin Meta etiketi eksik olduğundan Meta doğrulaması gerçekte olduğundan düşük görünebilir.</>}
      </Note>}
      <Panel title={t('Harcama ve Ciro')}>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={d.daily} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={T.border} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" {...AXIS} tickFormatter={tickDay} minTickGap={22} />
            <YAxis {...AXIS} width={52} tickFormatter={(v: number) => money(v, c, true)} />
            <Tooltip formatter={(v: any) => money(Number(v), c)} labelFormatter={l => `${t('Tarih')}: ${l}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="revenue" name={t('Net Ciro')} stroke={T.green} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="spend" name={t('Harcama')} stroke={T.accent} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title={t('Günlük MER')} pad={0}>
        <Table max={420} empty={t('Veri yok.')} rows={[...d.daily].reverse()} cols={[
          { label: 'Tarih', render: (r: any) => r.date },
          { label: 'Harcama', align: 'right', render: (r: any) => r.spend ? money(r.spend, c) : '—' },
          { label: 'Net Ciro', align: 'right', render: (r: any) => money(r.revenue, c) },
          { label: 'MER (Ciro / Reklam)', align: 'right', render: (r: any) => x2(r.mer) },
        ]} />
      </Panel>
    </div>
  );
}

// ── ANA BİLEŞEN ─────────────────────────────────────────────────────────────────────────
export default function ShopifyTab({ config, tab = 'overview', start, end }: { config: any; tab?: string; start?: string; end?: string }) {
  const clientId = config?.client_id;
  const shopDomain = (config?.platforms as any)?.shopify?.shopDomain || '';
  const enabled = (config?.platforms as any)?.shopify?.enabled !== false;
  if (!config?.platforms) return <Loader />;
  if (!clientId || !shopDomain || !enabled) return <DisconnectedState />;
  const p = { clientId, start, end };
  if (tab === 'products') return <ProductsTab {...p} />;
  if (tab === 'sales') return <SalesTab {...p} />;
  if (tab === 'orders') return <OrdersTab {...p} />;
  if (tab === 'returns') return <ReturnsTab {...p} />;
  if (tab === 'sources') return <SourcesTab {...p} />;
  if (tab === 'ads') return <AdsSalesTab {...p} />;
  return <OverviewTab {...p} />;
}
